import { mapReport, type RawReport } from '../../lib/vscanner/report-mapper';

export type ReportTab = 'all' | 'headers' | 'ssl' | 'js' | 'owasp';

export const REPORT_TABS: ReadonlyArray<{
  id: ReportTab;
  label: string;
  list: string;
}> = [
  { id: 'all', label: 'Overview', list: 'All issues' },
  { id: 'headers', label: 'Headers', list: 'Security headers' },
  { id: 'ssl', label: 'SSL / TLS', list: 'SSL / TLS' },
  { id: 'js', label: 'JavaScript', list: 'JavaScript' },
  { id: 'owasp', label: 'OWASP Top 10', list: 'OWASP Top 10' },
];

export type ItemSeverity = 'critical' | 'high' | 'medium' | 'low' | 'info';

export const ITEM_SEVERITY_LABEL: Record<ItemSeverity, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  info: 'Info',
};

interface ReportItem {
  severity: ItemSeverity;
  title: string;
  detail: string;
  fix: string;
  /** OWASP code, "INFO" or "Other". */
  cat: string;
  group: Exclude<ReportTab, 'all'>;
  location: string;
  parameter: string;
  evidence: string;
}

// Scanner finding types that carry no A-code, filed the way the design files them.
const CAT_BY_TYPE: Record<string, string> = {
  security_headers: 'A05:2021',
  weak_security_header: 'A05:2021',
  javascript_obfuscation: 'A08:2021',
  security_summary: 'INFO',
};
const GROUP_BY_TYPE: Record<string, ReportItem['group']> = {
  security_headers: 'headers',
  weak_security_header: 'headers',
  security_summary: 'headers',
  javascript_obfuscation: 'js',
};
const A_CODE = /^A\d\d:/;
const SEVERITY_RANK: Record<ItemSeverity, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
  info: 4,
};

const severityOf = (raw: string | undefined, fallback: ItemSeverity): ItemSeverity => {
  const s = (raw ?? '').toLowerCase();
  return s in SEVERITY_RANK ? (s as ItemSeverity) : fallback;
};

export function buildReportView(raw: RawReport, host: string) {
  const result = mapReport(raw);
  const ssl = raw.detailedSSL;

  const findings: ReportItem[] = (raw.findings ?? []).map((f) => ({
    severity: severityOf(f.severity, 'info'),
    title: f.title ?? '',
    detail: f.description ?? '',
    fix: f.remediation ?? '',
    cat: f.type && A_CODE.test(f.type) ? f.type : (CAT_BY_TYPE[f.type ?? ''] ?? 'Other'),
    group: GROUP_BY_TYPE[f.type ?? ''] ?? 'owasp',
    location: f.url ?? raw.url ?? '',
    parameter: f.parameter ?? '',
    evidence: f.evidence ?? '',
  }));

  const sslItems: ReportItem[] = (ssl?.vulnerabilities ?? [])
    .filter((v) => v.vulnerable)
    .map((v) => ({
      severity: severityOf(v.severity, 'low'),
      title: v.name,
      detail: v.description ?? '',
      fix: v.mitigation ?? '',
      cat: 'A02:2021',
      group: 'ssl' as const,
      location: `${ssl?.host ?? host}:${ssl?.port ?? 443}`,
      parameter: v.cve ?? '',
      evidence: '',
    }));

  // Stable sort keeps the scanner's own order inside one severity.
  const items = [...findings, ...sslItems].sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity]);

  return {
    result,
    items,
    jsCount: items.filter((i) => i.group === 'js').length,
  };
}

export type ReportView = ReturnType<typeof buildReportView>;

export const itemsForTab = (items: readonly ReportItem[], tab: ReportTab) =>
  tab === 'all' ? items : items.filter((i) => i.group === tab);
